import { useEffect, useId, useRef } from 'react';

/**
 * Shared interaction layer for controls that reveal a list or menu.
 *
 * Presentation intentionally remains with each consumer while migration is in
 * progress. This lets existing controls retain their visual contract while
 * sharing dismissal, Escape handling, focus restoration, and ARIA ids.
 */
export default function useDropdownPopover({
  open,
  onOpenChange,
  disabled = false,
  idPrefix = 'dropdown',
}) {
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const menuId = `${idPrefix}-${useId().replace(/:/g, '')}`;

  const setOpen = (nextOpen, { restoreFocus = false } = {}) => {
    onOpenChange(nextOpen);
    if (!nextOpen && restoreFocus) {
      window.requestAnimationFrame(() => triggerRef.current?.focus());
    }
  };

  const close = (options) => setOpen(false, options);
  const toggle = () => {
    if (!disabled) setOpen(!open);
  };

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        close();
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        close({ restoreFocus: true });
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]); // `open` intentionally controls the document listeners.

  const handleTriggerKeyDown = (event) => {
    if (disabled) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggle();
    }
  };

  return {
    rootRef,
    triggerRef,
    menuId,
    close,
    toggle,
    handleTriggerKeyDown,
  };
}
